// Cadence dividers on the paper are anchored to Africa/Johannesburg midnight
// so a Cape Town writer publishing at 22:00 SAST doesn't slide under
// "yesterday" for a reader in London. See project_paper_and_cadence memory
// for the "why".
import type { Cadence } from '../types/api';

const SAST_OFFSET_MS = 2 * 60 * 60 * 1000; // UTC+02:00, no DST in SA

// editionStart returns the UTC Date that marks the boundary of the current
// edition for the given cadence, in SAST terms:
//   daily   → 00:00 SAST today
//   weekly  → 00:00 SAST on the most recent Monday
//   monthly → 00:00 SAST on the 1st of the current month
// Anything with a timestamp >= editionStart is "in this edition"; older
// posts render below the divider.
export function editionStart(cadence: Cadence, now: Date = new Date()): Date {
    // Shift into SAST wall-clock via UTC arithmetic, snap to boundary, shift back.
    const sast = new Date(now.getTime() + SAST_OFFSET_MS);
    const y = sast.getUTCFullYear();
    const m = sast.getUTCMonth();
    const d = sast.getUTCDate();

    let boundarySast: Date;
    if (cadence === 'monthly') {
        boundarySast = new Date(Date.UTC(y, m, 1, 0, 0, 0, 0));
    } else if (cadence === 'weekly') {
        // ISO week: Monday = 1, Sunday = 7. Snap back to this week's Monday.
        const day = sast.getUTCDay(); // 0 = Sunday, 1 = Monday, ...
        const daysSinceMonday = (day + 6) % 7;
        boundarySast = new Date(Date.UTC(y, m, d - daysSinceMonday, 0, 0, 0, 0));
    } else {
        boundarySast = new Date(Date.UTC(y, m, d, 0, 0, 0, 0));
    }
    return new Date(boundarySast.getTime() - SAST_OFFSET_MS);
}

// dividerLabel is the section header for the "current edition" bucket.
export function dividerLabel(cadence: Cadence): string {
    switch (cadence) {
        case 'weekly':  return 'This Week';
        case 'monthly': return 'This Month';
        default:        return 'Today';
    }
}

// olderLabel is the header for the below-the-fold bucket.
export function olderLabel(cadence: Cadence): string {
    switch (cadence) {
        case 'weekly':  return 'Earlier Weeks';
        case 'monthly': return 'Earlier Months';
        default:        return 'Earlier';
    }
}

// splitByEdition partitions posts into current-edition and older buckets
// based on a post-timestamp getter and the community's cadence. Uses
// topic_day when available (rolling daily-topic articles) — same behaviour
// as the existing Paper.tsx day bucketer, just cadence-aware.
export function splitByEdition<T>(
    items: T[],
    getTs: (item: T) => string,
    cadence: Cadence,
    now: Date = new Date(),
): { current: T[]; older: T[] } {
    const boundary = editionStart(cadence, now).getTime();
    const current: T[] = [];
    const older: T[] = [];
    for (const it of items) {
        const t = new Date(getTs(it)).getTime();
        if (t >= boundary) current.push(it);
        else               older.push(it);
    }
    return { current, older };
}
